import { IQuotationsSuppliersSearch} from "./quotation-quotations-resource.service";
import { CalculationMode } from "../../../interfaces/IWenji";
import { EVatDisplayMode } from "libraries/service/vat/vat.interface";
import { EXRateType } from "libraries/interfaces/ITotal";
import { QuotationBlockCategoryCodeEnum } from "libraries/interfaces/quotation-block-category-code.enum";

export enum ChapterType {
    trip = "trip",
    fees = "fees",
    services = "services",
}

export interface IQuotationsQuotation {
    id: number;
    externalReference: IQuotationExternalReference;
    title: string;
    cost: IQuotationCost;
    retail?: IQuotationRetail;
    gain?: IQuotationCost;
    sections?: IQuotationSection[];
    travellers?: IQuotationTraveller[];
    begin?: string;
    isXRateValid: boolean;
    warnings?: IQuotationWarning[];
    details?: IQuotationDetails;
    vat: { rate: number };
    retailIncludingVat?: IQuotationCost;
    isReadOnly: boolean;
}

export interface IQuotationVatConfiguration {
    /** vatRate is a float. 20% --> 0.2 */
    vatRate: number;
    /** `null` when `vatRate == 0` */
    vatDisplayMode: EVatDisplayMode;
}

export interface IQuotationDetails {
    xrates: IQuotationXrates[];
}

export interface IQuotationXrates {
    id: number;
    rate: string;
    type: EXRateType;
    source: string;
    target: string;
}

export interface IQuotationWarning {
    id: number;
    quotation: number;
    message: string;
    isActive: boolean;
}

export interface IQuotationExternalReference {
    host: string;
    code: string;
    type?: string;
    detail?: IQuotationExternalReferenceDetail;
}

export interface IQuotationExternalReferenceDetail {
    booking: {
        idItem: number;
        idVersion: number;
    };
    quotation: {
        idItem: number;
        idVersion: number;
    };
}

export interface IQuotationRetail extends IQuotationCost {
    adjustment?: IQuotationCost;
    margin?: IQuotationMargin;
    markup?: IQuotationMargin;
    profit?: IQuotationCost;
    commission?: IQuotationCommission;
    displayMode?: EVatDisplayMode;
}

export interface IQuotationMargin {
    used: number;
    reference: number;
}

export interface IQuotationCommission {
    used: number;
    reference: number;
}

export interface IQuotationCost {
    currency?: string;
    value?: number;
    amount?: number;
    decimals?: number;
}

export interface IQuotationSection {
    order?: number;
    id: number;
    title: string;
    beginOffset?: number;
    duration?: number;
    chapterCode: ChapterType;
    externalReference?: IQuotationExternalReference;
    blocks?: IQuotationBlock[];
}

export interface IQuotationBlock {
    order?: number;
    id: number;
    title: string;
    category: QuotationBlockCategoryCodeEnum;
    section: number;
    beginOffset?: number;
    duration?: number;
    externalReference?: IQuotationExternalReference;
    retail?: IQuotationRetail;
    vat?: IQuotationCost;
    retailIncludingVat?: IQuotationCost;
    cost?: IQuotationCost;
    isChapo?: boolean;
    isOption?: boolean;
    comment?: string;
    totalPrices?: ITotalPrice[];
    supplier?: IQuotationsSuppliersSearch;
    travellerPrices?: IQuotationTravellerPrice[];
}

export interface IQuotationTravellerPrice {
    id: number;
    block: number;
    traveller: number;
    cost: IQuotationCost;
    retail: IQuotationRetail;
    retailIncludingVat: IQuotationCost;
}

export interface ITotalPrice {
    id: number;
    externalReference?: IQuotationExternalReference;
    cost: IQuotationCost;
    details: IDetailPrice[];
}

export interface IDetailPrice {
    id: number;
    total: number;
    externalReference?: IQuotationExternalReference;
    cost: IQuotationCost;
    travellers?: IDetailPriceTraveller[];
}

export interface IDetailPriceTraveller {
    id: number;
    traveller: number;
    priceDetail: number;
    cost?: IQuotationCost;
}

export interface IQuotationTraveller {
    firstName?: string;
    lastName?: string;
    travellerType?: string;
    externalReference?: IQuotationExternalReference;
    retail?: IQuotationRetail;
    vat?: IQuotationCost;
    retailIncludingVat?: IQuotationCost;
    cost?: IQuotationCost;
    id?: number;
    quotation?: number;
}

export interface IQuotationPriceTotal {
    id?: number;
    block?: number;
    externalReference?: IQuotationExternalReference;
    cost?: IQuotationCost;
    retail?: IQuotationRetail;
    travellers?: {
        id: number;
        traveller: number;
        cost: IQuotationCost;
    };
}

export enum QuotationCellsActions {
    updateDetailPriceTraveller = 'updateDetailPriceTraveller',
    updateDetailPrice = 'updateDetailPrice',
    updatePriceBlocTitle = 'updatePriceBlocTitle',
    updatePriceTotalCurrency = 'updatePriceTotalCurrency',
    createPriceBlock = 'createPriceBlock',
    removePriceBlock = 'removePriceBlock',
    addPriceBlockPlaceholder = 'addPriceBlockPlaceholder',
    updateSupplier = 'updateSupplier',
    createSupplier = 'createSupplier',
    updateQuotationProfit = 'updateQuotationProfit',
    updateQuotationMargin = 'updateQuotationMargin',
    updateQuotationVatConfiguration = 'updateQuotationVatConfiguration',
    updateBlockMargin = 'updateBlockMargin',
    updateBlockMarkup = 'updateBlockMarkup',
    updateQuotationCommission = 'updateQuotationCommission',
    updateBlockProfit = 'updateBlockProfit',
    updateBlockRetail = 'updateBlockRetail',
    updateQuotationTotalRetail = 'updateQuotationTotalRetail',
    updateTravellerTotalRetail = 'updateTravellerTotalRetail',
    updateTravellerAdjustment = 'updateTravellerAdjustment',
    updateQuotationAdjustment = 'updateQuotationAdjustment',
    refreshBlock = 'refreshBlock',
}

export interface ICellOutput {
    id?: number;
    itemType?: QuotationCellsActions;
    cost?: IQuotationCost;
    retail?: IQuotationRetail;
    price?: IQuotationCost;
    travellerId?: number;
    blockId?: number;
    title?: string;
    order?: number;
    chapterCode?: ChapterType;
    supplierId?: number;
    preferredCurrency?: string;
    currencies?: string[];
    categories?: string[];
    name?: string;
    margin?: IQuotationMargin;
    calculationMode?: CalculationMode;
    marginRequest?: { [key: string]: number };
    commissionRequest?: { [key: string]: number };
    vatConfiguration?: IQuotationVatConfiguration;
}

export interface ICellChange {
    output: ICellOutput;
    blockId?: number;
}

export interface ICellChangeResponse {
    quotation?: IQuotationsQuotation;
    newBlock?: IQuotationBlock;
    blockId?: number;
    chapterCode?: ChapterType;
    itemType: QuotationCellsActions;
    travellerId?: number;
}
